import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

export type Lang = "de" | "en";

const LangContext = createContext<Lang>("de");

export const isEnglishPath = (pathname: string) =>
  pathname === "/en" || pathname.startsWith("/en/");

/** Prefix a canonical (German) app path with /en when the language is English. */
export const localePath = (lang: Lang, path: string) => {
  if (lang !== "en") return path;
  if (path === "/") return "/en";
  return `/en${path.startsWith("/") ? "" : "/"}${path}`;
};

/** Strip the /en prefix from a pathname to get the canonical German path. */
export const stripLocale = (pathname: string) => {
  if (pathname === "/en") return "/";
  if (pathname.startsWith("/en/")) return pathname.slice(3);
  return pathname;
};

export interface AltPaths {
  /** German path of the current page, e.g. "/blog/mein-artikel" */
  de: string;
  /** English path (without /en prefix) of the current page */
  en: string | null;
}

interface AltPathsCtx {
  altPaths: AltPaths | null;
  setAltPaths: (p: AltPaths | null) => void;
}

const AltPathsContext = createContext<AltPathsCtx>({
  altPaths: null,
  setAltPaths: () => {},
});

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const lang: Lang = isEnglishPath(pathname) ? "en" : "de";
  const [altPaths, setAltPaths] = useState<AltPaths | null>(null);

  useEffect(() => {
    document.documentElement.lang = lang === "en" ? "en" : "de";
  }, [lang]);

  useEffect(() => {
    setAltPaths(null);
  }, [pathname]);

  const value = useMemo(() => ({ altPaths, setAltPaths }), [altPaths]);

  return (
    <LangContext.Provider value={lang}>
      <AltPathsContext.Provider value={value}>{children}</AltPathsContext.Provider>
    </LangContext.Provider>
  );
};

export const useAltPaths = () => useContext(AltPathsContext);

/** Pages with language-specific slugs register their counterpart paths here. */
export const useRegisterAltPaths = (paths: AltPaths | null) => {
  const { setAltPaths } = useContext(AltPathsContext);
  const de = paths?.de ?? null;
  const en = paths?.en ?? null;
  useEffect(() => {
    setAltPaths(de ? { de, en } : null);
    return () => setAltPaths(null);
  }, [de, en, setAltPaths]);
};

export const useLang = (): Lang => useContext(LangContext);

/** Pick the value matching the active language. */
export function useT<T>(de: T, en: T): T {
  return useLang() === "en" ? en : de;
}

/** Build language-aware links: lp("/blog") -> "/blog" or "/en/blog". */
export const useLocalePath = () => {
  const lang = useLang();
  return useMemo(() => (path: string) => localePath(lang, path), [lang]);
};

/** Anchor href that works from any page: "#kontakt" -> "/en/#kontakt" off-home. */
export const useAnchorHref = () => {
  const lang = useLang();
  const { pathname } = useLocation();
  const isHome = stripLocale(pathname) === "/";
  return useMemo(
    () => (hash: string) => (isHome ? hash : `${localePath(lang, "/")}${hash}`),
    [lang, isHome],
  );
};
